import { useCallback, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { InsightsSegment, InsightsRecording } from "@/types/insightsApi";
import { buildFilename, downloadSingle, downloadZip, RecordingFile } from "@/helpers/downloadRecordings";
import { Download, Loader2 } from "lucide-react";
import { SegmentItem } from "./InsightsReportsTypes";
import { MemoizedInsightsVirtualRow } from "./InsightsVirtualRow";
import { getRecordingUrl } from "@/helpers/insightsRecordingUrl";

// ── Per-row recordings state ───────────────────────────────────────────────────
type RecordingsState = InsightsRecording[] | "loading" | "error";

// ── Row heights ───────────────────────────────────────────────────────────────
const ROW_HEIGHT = 49;
const RECORDING_ROW_HEIGHT = 76;
const RECORDNG_ROW_GAP = 8;
const LOADING_EXPANDED_HEIGHT = 73;

function getExpandedHeight(state: RecordingsState | undefined): number {
  let calculatedHeight = ROW_HEIGHT;

  if (!state || state === "loading" || state === "error") {
    calculatedHeight += LOADING_EXPANDED_HEIGHT;
  } else if (state && state.length === 0) {
    calculatedHeight += LOADING_EXPANDED_HEIGHT;
  } else if (state && state.length > 0) {
    calculatedHeight += state.length * RECORDING_ROW_HEIGHT + (state.length - 1) * RECORDNG_ROW_GAP + ROW_HEIGHT;
  }

  return calculatedHeight;
}

// Grid column template — must match between header and rows
const GRID_COLS = "40px 9% 11% 9% 7% 17% 11% 12% 6% 6% 72px";

const COLUMNS: { key: keyof InsightsSegment; label: string }[] = [
  { key: "direction", label: "Direction" },
  { key: "customerPhone", label: "Customer Phone" },
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "agent", label: "Agent" },
  { key: "queue", label: "Queue" },
  { key: "department", label: "Department" },
  { key: "totalWaitingTime", label: "Wait" },
  { key: "totalTalkTime", label: "Talk" },
];

// ── Virtual items ──────────────────────────────────────────────────────────────

function buildSegmentItems(segments: InsightsSegment[], expandedSegmentSid: string | null): SegmentItem[] {
  return segments.map((record) => {
    if (record.segment === expandedSegmentSid) {
      return { type: "expanded", record };
    }
    return { type: "row", record };
  });
}

// Shared cell style
const CELL_STYLE = "flex items-center px-4 truncate text-sm";

// ── Main Table ─────────────────────────────────────────────────────────────────

interface InsightsReportsTableProps {
  segments: InsightsSegment[];
  isLoading: boolean;
  sortBy: { column: string; direction: "asc" | "desc" } | null;
  fetchRecordings: (taskId: string, signal?: AbortSignal) => Promise<InsightsRecording[]>;
  handleSort: (columnKey: keyof InsightsSegment) => void;
}

export function InsightsReportsTable({
  segments,
  isLoading,
  fetchRecordings,
  sortBy,
  handleSort,
}: InsightsReportsTableProps) {
  const [previousExpandedSegment, setPreviousExpandedSegment] = useState<string>("");
  const [selectedSegments, setSelectedSegments] = useState<Set<string>>(new Set());
  const [recordingsCache, setRecordingsCache] = useState<Map<string, RecordingsState>>(new Map());
  const scrollRef = useRef<HTMLDivElement>(null);

  const allSelected = segments.length > 0 && selectedSegments.size === segments.length;
  const someSelected = selectedSegments.size > 0 && !allSelected;

  const toggleSelectAll = useCallback(() => {
    setSelectedSegments(allSelected ? new Set() : new Set(segments.map((r) => r.segment)));
  }, [allSelected, segments]);

  const toggleSelect = useCallback((segmentSid: string) => {
    setSelectedSegments((prev) => {
      const next = new Set(prev);
      if (next.has(segmentSid)) {
        next.delete(segmentSid);
      } else {
        next.add(segmentSid);
      }
      return next;
    });
  }, []);

  const segmentItems = useMemo(
    () => buildSegmentItems(segments, previousExpandedSegment),
    [segments, previousExpandedSegment]
  );
  const virtualizer = useVirtualizer({
    count: segmentItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => {
      const item = segmentItems[i];
      if (!item || item.type !== "expanded") return ROW_HEIGHT;
      return getExpandedHeight(recordingsCache.get(item.record.segment));
    },
    overscan: 20,
  });

  const resizeItems = useCallback(
    (newCache: Map<string, RecordingsState>, newExpanded: string) => {
      virtualizer.resizeItem(
        segmentItems.findIndex((item) => item.record.segment === previousExpandedSegment),
        ROW_HEIGHT
      );
      if (newExpanded !== "") {
        const newRowHeight = getExpandedHeight(newCache.get(newExpanded || ""));

        virtualizer.resizeItem(
          segmentItems.findIndex((item) => item.record.segment === newExpanded),
          newRowHeight
        );
      }

      setPreviousExpandedSegment(newExpanded);
    },
    [virtualizer, segmentItems, previousExpandedSegment]
  );

  // Expand a row and trigger a recordings fetch if not already cached
  const toggleRow = useCallback(
    (record: InsightsSegment) => {
      const newExpanded = previousExpandedSegment === record.segment ? "" : record.segment;

      if (newExpanded && !recordingsCache.has(record.segment)) {
        const newCache = new Map(recordingsCache).set(record.segment, "loading");
        setRecordingsCache(newCache);
        resizeItems(newCache, newExpanded);

        fetchRecordings(record.segment)
          .then((recs) => {
            const newCache = new Map(recordingsCache).set(record.segment, recs);
            setRecordingsCache(newCache);
            resizeItems(newCache, newExpanded);
          })
          .catch(() => {
            const newCache = new Map(recordingsCache).set(record.segment, "error");
            setRecordingsCache(newCache);
            resizeItems(newCache, newExpanded);
          });
      } else {
        resizeItems(recordingsCache, newExpanded);
      }
    },
    [previousExpandedSegment, recordingsCache, fetchRecordings, resizeItems]
  );

  const clearRowExapansion = useCallback(() => {
    const newExpanded = "";
    resizeItems(recordingsCache, newExpanded);
  }, [recordingsCache, resizeItems]);

  const handleDownloadSelected = useCallback(async () => {
    const selected = segments.filter((r) => selectedSegments.has(r.segment));
    const CONCURRENCY = 30;

    // Step 1: Fetch recordings metadata for any selected rows not yet cached
    const localCache = new Map(recordingsCache);
    const uncached = selected.filter((r) => {
      const s = localCache.get(r.segment);
      return !s || s === "error";
    });

    for (let i = 0; i < uncached.length; i += CONCURRENCY) {
      const batch = uncached.slice(i, i + CONCURRENCY);
      const results = await Promise.allSettled(
        batch.map(async (record) => ({
          taskId: record.segment,
          recordings: await fetchRecordings(record.segment),
        }))
      );
      for (const result of results) {
        if (result.status === "fulfilled") {
          localCache.set(result.value.taskId, result.value.recordings);
        }
      }
    }
    setRecordingsCache(localCache);

    // Step 2: Flatten all recordings with filenames
    type DownloadEntry = { url: string; filename: string };
    const entries: DownloadEntry[] = [];
    for (const record of selected) {
      const recs = localCache.get(record.segment);
      if (!Array.isArray(recs)) continue;
      recs.forEach((rec) => {
        entries.push({
          url: getRecordingUrl(rec),
          filename: buildFilename(record.date, record.time, rec.sid),
        });
      });
    }

    if (entries.length === 0) return;

    // Step 3: Fetch audio binaries in parallel (concurrency 30)
    const allRecordings: RecordingFile[] = new Array(entries.length);
    for (let i = 0; i < entries.length; i += CONCURRENCY) {
      const batch = entries.slice(i, i + CONCURRENCY);
      const results = await Promise.all(
        batch.map(async (entry, batchIdx) => {
          const res = await fetch(entry.url);
          const buf = await res.arrayBuffer();
          return { index: i + batchIdx, file: { filename: entry.filename, data: new Uint8Array(buf) } };
        })
      );
      for (const { index, file } of results) {
        allRecordings[index] = file;
      }
    }

    if (allRecordings.length === 1) {
      downloadSingle(allRecordings[0]);
    } else {
      downloadZip(allRecordings);
    }
  }, [segments, selectedSegments, recordingsCache, fetchRecordings]);

  if (isLoading && segments.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (!isLoading && segments.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">No results found.</div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Result count + selection bar */}
      <div className="text-muted-foreground flex shrink-0 items-center gap-3 border-b px-4 py-2 text-xs">
        <span>
          {segments.length.toLocaleString()} result{segments.length !== 1 ? "s" : ""}
          {isLoading && <Loader2 className="ml-1.5 inline size-3 animate-spin" />}
        </span>
        {selectedSegments.size > 0 && (
          <>
            <span className="text-foreground font-medium">· {selectedSegments.size.toLocaleString()} selected</span>
            <Button variant="outline" size="sm" className="h-6 gap-1.5 px-2 text-xs" onClick={handleDownloadSelected}>
              <Download className="size-3" />
              Download selected
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setSelectedSegments(new Set())}
            >
              Clear selection
            </Button>
          </>
        )}
      </div>

      {/* Scrollable area */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        {/* Sticky header */}
        <div className="bg-muted sticky top-0 z-10 grid border-b" style={{ gridTemplateColumns: GRID_COLS }}>
          <div className="flex items-center justify-center px-3">
            <Checkbox
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={toggleSelectAll}
              aria-label="Select all rows"
            />
          </div>
          {COLUMNS.map((col) => (
            <Button
              variant="ghost"
              key={col.key}
              className="text-muted-foreground justify-start truncate px-4 py-2.5 text-xs font-medium tracking-wide uppercase"
              onClick={() => {
                clearRowExapansion();
                handleSort(col.key);
              }}
            >
              {col.label}
              {sortBy?.column === col.key && (
                <span className="ml-1 inline-block">{sortBy.direction === "asc" ? "▲" : "▼"}</span>
              )}
            </Button>
          ))}
          <div />
        </div>

        {/* Virtualised rows */}
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((vItem) => {
            const item = segmentItems[vItem.index];
            const { record } = item;
            const isExpanded = previousExpandedSegment === record.segment;
            const isSelected = selectedSegments.has(record.segment);

            return (
              <MemoizedInsightsVirtualRow
                key={item.record.segment}
                virtualizer={virtualizer}
                virtualItem={vItem}
                segmentItem={item}
                recordingsCache={recordingsCache}
                cellStyle={CELL_STYLE}
                gridColsStyle={GRID_COLS}
                isExpanded={isExpanded}
                isSelected={isSelected}
                toggleRow={toggleRow}
                toggleSelect={toggleSelect}
                baseRowHeight={ROW_HEIGHT}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
